import React, { useEffect, useMemo, useState } from 'react';
import './Dashboard.css';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import { useFetch } from "../hooks/useFetch";
import Header from "./common/Header";
import { useTranslation } from 'react-i18next';
import Sidebar from "./common/Sidebar";

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'es';
  const locale = lang.startsWith('es') ? 'es-CL' : lang.startsWith('en') ? 'en-US' : lang;
  const CURRENCY = 'CLP';

  const [expensesData] = useFetch('/transactions', { defaultValue: [], hasPaging: true, defaultLimit: 500 });

  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    const list = Array.isArray(expensesData.transactions) ? expensesData.transactions
      : Array.isArray(expensesData) ? expensesData : [];
    setExpenses(list);
  }, [expensesData]);

  const getDueDate = (e) => {
    const raw =
      e.installment_due ||
      e.data?.installment_due ||
      e.dueDate ||
      e.data?.dueDate ||
      e.date;
    if (!raw) return null;
    const d = typeof raw === 'string' ? new Date(raw) : (raw instanceof Date ? raw : null);
    if (!d) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const cuotas = useMemo(() => {
    if (!Array.isArray(expenses) || expenses.length === 0) return [];
    return expenses
      .map(e => {
        const str = (typeof e.installment === 'string' && e.installment) || (typeof e.data?.installment === 'string' && e.data.installment) || null;
        if (!str || !str.includes('/')) return null;
        const m = str.match(/(\d+)\s*\/\s*(\d+)/);
        if (!m) return null;
        const n = Number(m[1]), total = Number(m[2]);
        if (!total || n > total) return null;
        const due = getDueDate(e);
        if (!due) return null;
        return {
          merchant: e.data?.merchant || e.merchant || e.description || '—',
          amount: Number(e.amount) || 0,
          n, total,
          due
        };
      })
      .filter(Boolean);
  }, [expenses]);

  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const [viewMode, setViewMode] = useState('year');

  const addYears = (date, n) => new Date(date.getFullYear() + n, date.getMonth(), 1);

  const cuotasByMonth = useMemo(() => {
    const out = Array.from({ length: 12 }, () => []);
    const yr = calMonth.getFullYear();
    cuotas.forEach(c => {
      const remaining = Math.max(0, (c.total || 0) - (c.n || 0));
      for (let k = 0; k < remaining; k++) {
        const instDue = new Date(c.due);
        instDue.setMonth(instDue.getMonth() + k);
        if (instDue.getFullYear() !== yr) continue;
        const m = instDue.getMonth();
        out[m].push({
          ...c,
          due: instDue,
          n: (c.n || 0) + k + 1,
          projected: true
        });
      }
    });
    out.forEach(list => list.sort((a, b) => b.amount - a.amount));
    return out;
  }, [cuotas, calMonth]);

  const cuotasByDay = useMemo(() => {
    const out = {};
    cuotas.forEach(c => {
      const remaining = Math.max(0, (c.total || 0) - (c.n || 0));
      for (let k = 0; k < remaining; k++) {
        const instDue = new Date(c.due);
        instDue.setMonth(instDue.getMonth() + k);
        if (instDue.getFullYear() !== calMonth.getFullYear() || instDue.getMonth() !== calMonth.getMonth()) continue;
        const key = `${instDue.getFullYear()}-${String(instDue.getMonth() + 1).padStart(2, '0')}-${String(instDue.getDate()).padStart(2, '0')}`;
        (out[key] ||= []).push({
          ...c,
          due: instDue,
          n: (c.n || 0) + k + 1,
          projected: true
        });
      }
    });
    Object.values(out).forEach(list => list.sort((a, b) => b.amount - a.amount));
    return out;
  }, [cuotas, calMonth]);

  const localeYear = useMemo(() => calMonth.toLocaleDateString(locale, { year: 'numeric' }), [calMonth, locale]);
  const headerLabel = useMemo(() => (viewMode === 'year' ? localeYear : calMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' })), [viewMode, localeYear, calMonth, locale]);

  const goPrev = () => setCalMonth(m => (viewMode === 'year' ? addYears(m, -1) : new Date(m.getFullYear(), m.getMonth() - 1, 1)));
  const goNext = () => setCalMonth(m => (viewMode === 'year' ? addYears(m, 1) : new Date(m.getFullYear(), m.getMonth() + 1, 1)));

  return (
    <div className="dashboard-container overview">
      <Header title={t('installments.title', 'Cuotas')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-main">

          <div className="dashboard-section" style={{ marginBottom: '0.75rem', gridColumn: '1 / -1' }}>
            <h2 className="dashboard-section-title" style={{ fontSize: '1.2rem', margin: 0 }}>
              {t('installments.title', 'Cuotas')}
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {viewMode === 'month' && (
                <button onClick={() => setViewMode('year')}>⟵ {t('common.back', 'Volver')}</button>
              )}
              <button onClick={goPrev}>◀</button>
              <strong style={{ minWidth: 140, textTransform: 'capitalize' }}>{headerLabel}</strong>
              <button onClick={goNext}>▶</button>
            </div>
          </div>

          {viewMode === 'year' && (
            <div className="dashboard-section" style={{ gridColumn: '1 / -1', width: '100%' }}>
              <div className="year-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                {Array.from({ length: 12 }).map((_, mIdx) => {
                  const items = cuotasByMonth[mIdx] || [];
                  const monthlyTotal = items.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
                  const monthName = new Date(calMonth.getFullYear(), mIdx, 1).toLocaleDateString(locale, { month: 'long' });
                  return (
                    <div
                      key={mIdx}
                      className={items.length === 0 ? 'month-empty' : ''}
                      onClick={items.length === 0 ? undefined : () => { setCalMonth(new Date(calMonth.getFullYear(), mIdx, 1)); setViewMode('month'); }}
                      role={items.length === 0 ? undefined : 'button'}
                      aria-disabled={items.length === 0 ? true : undefined}
                      style={{ cursor: items.length === 0 ? 'default' : 'pointer', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem', background: '#fff', display: 'flex', flexDirection: 'column', gap: '0.35rem', minHeight: 120 }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{monthName}</span>
                        {items.length > 0 && (
                          <span style={{ fontSize: 11, borderRadius: 6, padding: '0 6px', background: '#EEF2FF', color: '#1E3A8A' }}>
                            {items.length}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexGrow: 1, minHeight: 0 }}>
                        {items.slice(0, 5).map((c, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                            <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                              {c.merchant} <span style={{ opacity: c.projected ? 0.5 : 0.6 }}>({c.n}/{c.total}{c.projected ? '·' : ''})</span>
                            </span>
                            <strong style={{ fontSize: 12 }}>
                              {c.amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                            </strong>
                          </div>
                        ))}
                      </div>
                      {items.length > 0 && (
                        <div style={{ marginTop: 'auto', paddingTop: 6, borderTop: '1px solid #eef2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, opacity: 0.7 }}>{t('installments.monthTotal', 'Total')}</span>
                          <strong style={{ fontSize: 12 }}>
                            {monthlyTotal.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                          </strong>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {viewMode === 'month' && (
            <div className="dashboard-section" style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ margin: '0 0 0.5rem', textTransform: 'capitalize' }}>
                {calMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 }}>
                {(() => {
                  const entries = Object.entries(cuotasByDay)
                    .filter(([key]) => key.startsWith(`${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, '0')}-`))
                    .sort(([a], [b]) => new Date(a) - new Date(b));

                  if (!entries.length) {
                    return <div style={{ opacity: 0.7 }}>{t('installments.none', 'No hay cuotas para este mes')}</div>;
                  }

                  return entries.map(([dayKey, list]) => (
                    <div key={dayKey} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem', background: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <strong>{new Date(dayKey).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}</strong>
                        <span style={{ fontSize: 11, borderRadius: 6, padding: '0 6px', background: '#EEF2FF', color: '#1E3A8A' }}>{list.length}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {list.map((c, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '72%' }}>
                              {c.merchant} <span style={{ opacity: c.projected ? 0.5 : 0.6 }}>({c.n}/{c.total}{c.projected ? ' · ' + t('installments.projected', 'proj') : ''})</span>
                            </span>
                            <strong style={{ fontSize: 13 }}>{c.amount.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}